class ModelsTelemetryClient {
    constructor(options = {}) {
        this.options = {
            endpoint: '/api/telemetry/models-page',
            flushIntervalMs: 5000,
            maxBatchSize: 25,
            maxQueueSize: 400,
            resolveAccessToken: null,
            ...options
        };
        this.queue = [];
        this.flushTimer = null;
        this.inFlight = false;
        this.started = false;
        this.lastError = null;
        this.stats = {
            queuedEvents: 0,
            droppedEvents: 0,
            sentBatches: 0,
            failedBatches: 0,
            lastFlushAt: null
        };

        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
        this.handlePageHide = this.handlePageHide.bind(this);
    }

    configure(options = {}) {
        this.options = {
            ...this.options,
            ...options
        };
    }

    start() {
        if (this.started) {
            return;
        }
        this.started = true;
        this.scheduleFlush();

        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this.handleVisibilityChange);
        }
        if (typeof window !== 'undefined') {
            window.addEventListener('pagehide', this.handlePageHide);
            window.addEventListener('beforeunload', this.handlePageHide);
        }
    }

    stop() {
        if (!this.started) {
            return;
        }
        this.started = false;
        if (this.flushTimer) {
            clearTimeout(this.flushTimer);
            this.flushTimer = null;
        }

        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        }
        if (typeof window !== 'undefined') {
            window.removeEventListener('pagehide', this.handlePageHide);
            window.removeEventListener('beforeunload', this.handlePageHide);
        }
    }

    handleVisibilityChange() {
        if (typeof document === 'undefined') {
            return;
        }
        if (document.visibilityState === 'hidden') {
            this.flush({ immediate: true, useBeacon: true, reason: 'visibility_hidden' });
        }
    }

    handlePageHide() {
        this.flush({ immediate: true, useBeacon: true, reason: 'pagehide' });
    }

    enqueue(event) {
        if (!event || typeof event !== 'object') {
            return;
        }

        if (this.queue.length >= this.options.maxQueueSize) {
            this.queue.shift();
            this.stats.droppedEvents += 1;
        }

        this.queue.push({
            ...event,
            clientCapturedAt: new Date().toISOString()
        });
        this.stats.queuedEvents += 1;

        if (this.queue.length >= this.options.maxBatchSize) {
            this.flush({ immediate: true, reason: 'batch_full' });
        } else {
            this.scheduleFlush();
        }
    }

    scheduleFlush() {
        if (this.flushTimer) {
            return;
        }

        this.flushTimer = setTimeout(() => {
            this.flushTimer = null;
            this.flush({ reason: 'interval' });
        }, this.options.flushIntervalMs);
    }

    async resolveAccessToken() {
        if (typeof this.options.resolveAccessToken !== 'function') {
            return null;
        }
        try {
            const token = await this.options.resolveAccessToken();
            return token || null;
        } catch (_) {
            return null;
        }
    }

    buildPayload(events, reason = 'interval') {
        return {
            source: 'models_page',
            reason,
            sentAt: new Date().toISOString(),
            events
        };
    }

    flushWithBeacon(payload) {
        if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
            return false;
        }

        try {
            const body = JSON.stringify(payload);
            const blob = new Blob([body], { type: 'application/json' });
            return navigator.sendBeacon(this.options.endpoint, blob);
        } catch (_) {
            return false;
        }
    }

    async flushWithFetch(payload, useKeepalive = false) {
        const headers = {
            'Content-Type': 'application/json'
        };
        const firstCorrelationId = payload?.events?.[0]?.correlationId;
        if (firstCorrelationId) {
            headers['x-correlation-id'] = firstCorrelationId;
        }
        const token = await this.resolveAccessToken();
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(this.options.endpoint, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload),
            keepalive: !!useKeepalive
        });
        return response.ok;
    }

    async flush(options = {}) {
        if (this.inFlight) {
            return false;
        }
        if (this.queue.length === 0) {
            return true;
        }

        const {
            immediate = false,
            useBeacon = false,
            reason = 'interval'
        } = options;

        this.inFlight = true;
        const batchSize = immediate
            ? this.queue.length
            : Math.min(this.queue.length, this.options.maxBatchSize);
        const events = this.queue.splice(0, batchSize);
        const payload = this.buildPayload(events, reason);

        try {
            let success = false;
            if (useBeacon) {
                success = this.flushWithBeacon(payload);
            }

            if (!success) {
                success = await this.flushWithFetch(payload, immediate || useBeacon);
            }

            if (!success) {
                throw new Error('Telemetry ingest returned non-OK status');
            }

            this.stats.sentBatches += 1;
            this.stats.lastFlushAt = new Date().toISOString();
            this.lastError = null;
            return true;
        } catch (error) {
            this.lastError = error;
            this.stats.failedBatches += 1;

            // Re-queue batch at front (up to queue limit)
            this.queue = events.concat(this.queue).slice(0, this.options.maxQueueSize);
            return false;
        } finally {
            this.inFlight = false;
            if (this.queue.length > 0) {
                this.scheduleFlush();
            }
        }
    }

    getStats() {
        return {
            ...this.stats,
            pendingQueue: this.queue.length,
            lastError: this.lastError ? (this.lastError.message || String(this.lastError)) : null
        };
    }
}

const modelsTelemetryClient = new ModelsTelemetryClient();

function createModelsTelemetrySink(options = {}) {
    modelsTelemetryClient.configure(options);
    modelsTelemetryClient.start();
    return (event) => {
        modelsTelemetryClient.enqueue(event);
    };
}

export {
    ModelsTelemetryClient,
    modelsTelemetryClient,
    createModelsTelemetrySink
};
